import { useContext, useEffect, useReducer } from "react";
import { sendRequest } from "../../common/http";
import translate from "@/i18n/translate";
import {
  WmProgressMonitor,
  WmProgressSlice,
  WmProgressIndicator,
  WmTabList,
  WmTabItem
} from "@watermarkinsights/ripple-react";
import ReportingYears, {
  dateWithoutTimezone
} from "../common/elements/ReportingYears";
import { WmTabPanel } from "@watermarkinsights/ripple-react";
import { useIntl } from "react-intl";
import {
  ReportingYear,
  MembershipInterface
} from "../common/interfaces/Interface";
import WidgetLoader from "../common/elements/WidgetLoader";
import ApiError from "../common/elements/ApiError";
import { AppContext, AppContextInterface } from "../../contexts/AppContext";
import { defaultReportingYear as emptyReportingYear } from "../common/interfaces/Interface";

export interface CurricullumProposalsProps {
  reportingYears: ReportingYear[] | null;
  defaultReportingYear: ReportingYear;
  mappingId: string;
  onComponentLoad: (component: string) => void;
  resetLoadedComponent: (component: string) => void;
  ryApiError: boolean;
}

interface Proposals {
  newAccepted: number;
  newInReview: number;
  revisedAccepted: number;
  revisedInReview: number;
}

interface CurriculumChanges {
  totalProgramProposals: number;
  totalCourseProposals: number;
  programProposals: Proposals;
  courseProposals: Proposals;
}

interface CurriculumProposalsState {
  selectedReportingYear: ReportingYear;
  tab: string;
  curriculumChanges: CurriculumChanges | null;
  showLoader: boolean;
  apiError: boolean;
}
interface CurriculumProposalsAction {
  type: string;
  selectedReportingYear: ReportingYear;
  tab: string;
  curriculumChanges: CurriculumChanges | null;
  showLoader: boolean;
  apiError: boolean;
}
const curriculumProposalsAction = {
  type: "",
  selectedReportingYear: emptyReportingYear,
  tab: "",
  curriculumChanges: null,
  showLoader: true,
  apiError: false
};
const reducer = (
  state: CurriculumProposalsState,
  action: CurriculumProposalsAction
) => {
  switch (action.type) {
    case "selectReportingYear":
      return {
        ...state,
        selectedReportingYear: action.selectedReportingYear
      };
    case "setSelectedTab":
      return { ...state, tab: action.tab };
    case "setCurriculumData":
      return { ...state, curriculumChanges: action.curriculumChanges };
    case "setShowLoader":
      return {
        ...state,
        showLoader: action.showLoader
      };
    case "setApiError":
      return { ...state, apiError: action.apiError };
    default:
      return state;
  }
};
export default function CurriculumProposals({
  reportingYears,
  defaultReportingYear,
  mappingId,
  onComponentLoad,
  resetLoadedComponent,
  ryApiError
}: CurricullumProposalsProps) {
  const initialState = {
    selectedReportingYear: defaultReportingYear,
    tab: "program-proposals",
    curriculumChanges: null,
    showLoader: true,
    apiError: false
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const { membership } = useContext<AppContextInterface>(AppContext);
  const selectReportingYear = (year: ReportingYear) => {
    dispatch({
      ...curriculumProposalsAction,
      type: "selectReportingYear",
      selectedReportingYear: year
    });
  };

  const onWmTabSelected = (event: any) => {
    dispatch({
      ...curriculumProposalsAction,
      type: "setSelectedTab",
      tab: event.detail.tabId
    });
  };

  const tabOption = [
    {
      name: translate("app.missionAndOutcomes.programs"),
      id: "program-proposals"
    },
    {
      name: translate("app.missionAndOutcomes.courses"),
      id: "course-proposals"
    }
  ];

  const resetApiErrors = () => {
    dispatch({
      ...curriculumProposalsAction,
      type: "setApiError",
      apiError: false
    });
  };

  useEffect(() => {
    if (state.selectedReportingYear && state.selectedReportingYear.startDate) {
      dispatch({
        ...curriculumProposalsAction,
        type: "setShowLoader",
        showLoader: true
      });
      resetLoadedComponent("curriculumProposals");
      resetApiErrors();
      const applyResult = (result: CurriculumChanges) => {
        dispatch({
          ...curriculumProposalsAction,
          type: "setShowLoader",
          showLoader: false
        });
        dispatch({
          ...curriculumProposalsAction,
          type: "setCurriculumData",
          curriculumChanges: result
        });
        onComponentLoad("curriculumProposals");
      };
      const onError = () => {
        dispatch({
          ...curriculumProposalsAction,
          type: "setShowLoader",
          showLoader: false
        });
        dispatch({
          ...curriculumProposalsAction,
          type: "setApiError",
          apiError: true
        });
        onComponentLoad("curriculumProposals");
      };
      sendRequest(
        {
          uri:
            "/cs/curricular_changes" +
            "?mapping_id=" +
            mappingId +
            "&start_date=" +
            dateWithoutTimezone(state.selectedReportingYear?.startDate) +
            "&end_date=" +
            dateWithoutTimezone(state.selectedReportingYear?.endDate),
          camelize: true
        },
        applyResult,
        onError
      );
    }
  }, [state.selectedReportingYear, mappingId]);

  useEffect(() => {
    selectReportingYear(defaultReportingYear);
  }, [defaultReportingYear]);

  const showData = () => !state.showLoader && !ryApiError && !state.apiError;
  const showLoader = () => state.showLoader && !ryApiError && !state.apiError;

  return (
    <div className="paper-card program-ilo-widget">
      <div className="header">
        <div className="info">
          <h3 className="title">
            {translate("app.institution.curriculumProposalsHeader")}
          </h3>
          <p className="description">
            {translate("app.institution.curriculumProposalsDescription")}
          </p>
        </div>
      </div>
      <div
        className={
          reportingYears?.length === 0 ? "body flex-row center-element" : "body"
        }
      >
        {reportingYears?.length === 0 && (
          <ReportingYearEmptyState membership={membership} />
        )}
        {!ryApiError &&
        reportingYears &&
        reportingYears?.length > 0 &&
        state.selectedReportingYear.startDate ? (
          <>
            <ReportingYears
              reportingYears={reportingYears}
              selectedReportingYear={state.selectedReportingYear}
              selectReportingYear={selectReportingYear}
              key={state.selectedReportingYear.uuid}
            />

            {showLoader() ? (
              <WidgetLoader
                loaderContainerClasses="loader-container"
                loaderClasses="widget-loader"
              />
            ) : (
              ""
            )}
            {showData() ? (
              <div className="tabbar curriculam-tab">
                <WmTabList
                  id="curriculum-tab"
                  selectedTab={state.tab}
                  onWmTabSelected={onWmTabSelected}
                >
                  {tabOption.map(
                    (tab: { name: JSX.Element; id: string }, index: number) => (
                      <WmTabItem tabId={tab.id} key={tab.id + (index + 1)}>
                        {tab.name}
                      </WmTabItem>
                    )
                  )}
                </WmTabList>
              </div>
            ) : (
              ""
            )}
          </>
        ) : (
          ""
        )}
        {ryApiError && <ApiError />}
        {state.apiError && !ryApiError ? (
          <ApiError containerClasses="api-error curriculum-proposals-api-error" />
        ) : (
          ""
        )}
        {showData() ? (
          <>
            <WmTabPanel
              className="tab-panel"
              id="program-proposals"
              tabId="program-proposals"
            >
              <div className="curriculum-panel">
                {state.curriculumChanges &&
                state.curriculumChanges.totalProgramProposals > 0 ? (
                  <h4 className="title-curriculum-chart">
                    {translate("app.institution.curriculum.programProposals")}
                  </h4>
                ) : (
                  ""
                )}
                {state.curriculumChanges?.totalProgramProposals === 0 ? (
                  <NoProgramProposals />
                ) : (
                  ""
                )}
                {state.curriculumChanges &&
                state.curriculumChanges.totalProgramProposals > 0 ? (
                  <>
                    <p className="org totals-curriculum-chart">
                      {translate("app.institution.curriculum.totalProposals", {
                        total: state.curriculumChanges.totalProgramProposals
                      })}
                    </p>
                    <LegendWrapper />
                    <CurriculumProposalsProgramsChart
                      proposals={state.curriculumChanges.programProposals}
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            </WmTabPanel>

            <WmTabPanel
              className="tab-panel"
              id="course-proposals"
              tabId="course-proposals"
            >
              <div className="curriculum-panel">
                {state.curriculumChanges &&
                state.curriculumChanges.totalCourseProposals > 0 ? (
                  <h4 className="title-curriculum-chart">
                    {translate("app.institution.curriculum.courseProposals")}
                  </h4>
                ) : (
                  ""
                )}
                {state.curriculumChanges?.totalCourseProposals === 0 ? (
                  <NoCourseProposals />
                ) : (
                  ""
                )}
                {state.curriculumChanges &&
                state.curriculumChanges.totalCourseProposals > 0 ? (
                  <>
                    <p className="org totals-curriculum-chart">
                      {translate("app.institution.curriculum.totalProposals", {
                        total: state.curriculumChanges.totalCourseProposals
                      })}
                    </p>
                    <LegendWrapper />
                    <CurriculumProposalsCoursesChart
                      proposals={state.curriculumChanges.courseProposals}
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            </WmTabPanel>
          </>
        ) : (
          ""
        )}
      </div>
    </div>
  );
}

interface ProposalChartProps {
  proposals: Proposals;
}
const CurriculumProposalsProgramsChart = ({
  proposals: programProposals
}: ProposalChartProps) => {
  const accepted = useIntl().formatMessage({
    id: "app.institution.curriculum.accepted"
  });
  const inReview = useIntl().formatMessage({
    id: "app.institution.curriculum.inReview"
  });
  const acceptedPopoverTextRevised = useIntl().formatMessage({
    id: "app.institution.curriculum.acceptedPopoverTextRevised"
  });

  const inReviewPopoverTextRevised = useIntl().formatMessage({
    id: "app.institution.curriculum.inReviewPopoverRevised"
  });
  const acceptedPopoverTextNew = useIntl().formatMessage({
    id: "app.institution.curriculum.acceptedPopoverTextNew"
  });

  const inReviewPopoverTextNew = useIntl().formatMessage({
    id: "app.institution.curriculum.inReviewPopoverNew"
  });
  const newProgram = useIntl().formatMessage({
    id: "app.institution.curriculum.newProgram"
  });

  const reviseProgram = useIntl().formatMessage({
    id: "app.institution.curriculum.revisedProgram"
  });

  const newTotal = programProposals.newAccepted + programProposals.newInReview;
  const revisedTotal =
    programProposals.revisedAccepted + programProposals.revisedInReview;
  return (
    <>
      {programProposals && (
        <WmProgressMonitor id="curriculum-Programs">
          {programProposals.newAccepted || programProposals.newInReview ? (
            <WmProgressIndicator label={newProgram} key="new-program-proposals">
              <WmProgressSlice
                legend={accepted}
                amount={`${programProposals.newAccepted}`}
                popoverTitle={popover(programProposals.newAccepted, newTotal)}
                popoverText={acceptedPopoverTextNew}
              />
              <WmProgressSlice
                legend={inReview}
                amount={`${programProposals.newInReview}`}
                popoverTitle={popover(programProposals.newInReview, newTotal)}
                popoverText={inReviewPopoverTextNew}
              />
            </WmProgressIndicator>
          ) : (
            <div>
              <div className="proposals-label">{newProgram}</div>
              <div className="proposals-empty">
                {translate("app.institution.curriculum.newProgramEmptyState")}
              </div>
            </div>
          )}
          {programProposals.revisedAccepted ||
          programProposals.revisedInReview ? (
            <WmProgressIndicator
              label={reviseProgram}
              key="revise-program-proposals"
            >
              <WmProgressSlice
                legend={accepted}
                amount={`${programProposals.revisedAccepted}`}
                popoverTitle={popover(
                  programProposals.revisedAccepted,
                  revisedTotal
                )}
                popoverText={acceptedPopoverTextRevised}
              />
              <WmProgressSlice
                legend={inReview}
                amount={`${programProposals.revisedInReview}`}
                popoverTitle={popover(
                  programProposals.revisedInReview,
                  revisedTotal
                )}
                popoverText={inReviewPopoverTextRevised}
              />
            </WmProgressIndicator>
          ) : (
            <div>
              <div className="proposals-label">{reviseProgram}</div>
              <div className="proposals-empty">
                {translate(
                  "app.institution.curriculum.revisedProgramEmptyState"
                )}
              </div>
            </div>
          )}
        </WmProgressMonitor>
      )}
    </>
  );
};

const LegendWrapper = () => {
  return (
    <div className="legend-wrapper">
      <div className="legend" aria-hidden="true">
        <div className="legend-item">
          <div
            className="legend-color"
            style={{ backgroundColor: "rgb(107, 107, 107)" }}
          ></div>
          <div className="legend-text">
            {translate("app.institution.curriculum.inReview")}
          </div>
        </div>
        <div className="legend-item">
          <div
            className="legend-color"
            style={{ backgroundColor: "rgb(8, 128, 0)" }}
          ></div>
          <div className="legend-text">
            {translate("app.institution.curriculum.accepted")}
          </div>
        </div>
      </div>
    </div>
  );
};

const CurriculumProposalsCoursesChart = ({
  proposals: courseProposals
}: ProposalChartProps) => {
  const accepted = useIntl().formatMessage({
    id: "app.institution.curriculum.accepted"
  });
  const inReview = useIntl().formatMessage({
    id: "app.institution.curriculum.inReview"
  });
  const acceptedPopoverTextCourseRevised = useIntl().formatMessage({
    id: "app.institution.curriculum.acceptedPopoverTextCourseRevised"
  });

  const inReviewPopoverTextCourseRevised = useIntl().formatMessage({
    id: "app.institution.curriculum.inReviewPopoverTextCourseRevised"
  });
  const acceptedPopoverTextCourseNew = useIntl().formatMessage({
    id: "app.institution.curriculum.acceptedPopoverTextCourseNew"
  });

  const inReviewPopoverTextCourseNew = useIntl().formatMessage({
    id: "app.institution.curriculum.inReviewPopoverTextCourseNew"
  });
  const newCourse = useIntl().formatMessage({
    id: "app.institution.curriculum.newCourse"
  });

  const reviseCourse = useIntl().formatMessage({
    id: "app.institution.curriculum.revisedCourse"
  });

  const newTotal = courseProposals.newAccepted + courseProposals.newInReview;
  const revisedTotal =
    courseProposals.revisedAccepted + courseProposals.revisedInReview;

  return (
    <>
      {courseProposals && (
        <WmProgressMonitor id="curriculum-courses">
          {courseProposals.newAccepted || courseProposals.newInReview ? (
            <WmProgressIndicator label={newCourse} key="new-course-proposals">
              <WmProgressSlice
                legend={accepted}
                amount={`${courseProposals.newAccepted}`}
                popoverTitle={popover(courseProposals.newAccepted, newTotal)}
                popoverText={acceptedPopoverTextCourseNew}
              />
              <WmProgressSlice
                legend={inReview}
                amount={`${courseProposals.newInReview}`}
                popoverTitle={popover(courseProposals.newInReview, newTotal)}
                popoverText={inReviewPopoverTextCourseNew}
              />
            </WmProgressIndicator>
          ) : (
            <div>
              <div className="proposals-label">{newCourse}</div>
              <div className="proposals-empty">
                {translate("app.institution.curriculum.newCourseEmptyState")}
              </div>
            </div>
          )}
          {courseProposals.revisedAccepted ||
          courseProposals.revisedInReview ? (
            <WmProgressIndicator
              label={reviseCourse}
              key="revise-course-proposals"
            >
              <WmProgressSlice
                legend={accepted}
                amount={`${courseProposals.revisedAccepted}`}
                popoverTitle={popover(
                  courseProposals.revisedAccepted,
                  revisedTotal
                )}
                popoverText={acceptedPopoverTextCourseRevised}
              />
              <WmProgressSlice
                legend={inReview}
                amount={`${courseProposals.revisedInReview}`}
                popoverTitle={popover(
                  courseProposals.revisedInReview,
                  revisedTotal
                )}
                popoverText={inReviewPopoverTextCourseRevised}
              />
            </WmProgressIndicator>
          ) : (
            <div>
              <div className="proposals-label">{reviseCourse}</div>
              <div className="proposals-empty">
                {translate(
                  "app.institution.curriculum.revisedCourseEmptyState"
                )}
              </div>
            </div>
          )}
        </WmProgressMonitor>
      )}
    </>
  );
};

const popover = (amount: number | undefined, total: number | undefined) => {
  return `${amount}/${total}`;
};

const ReportingYearEmptyState = ({
  membership
}: {
  membership: MembershipInterface | null;
}) => {
  return (
    <div className="empty-state-curriculum-widget flex-row center-element">
      <div>
        <div className="empty-state-title">
          {translate("app.continuousImprovement.noAcademicYearTitle")}
        </div>
        {membership && membership.userRole === "admin" && adminEmptyState()}
        {membership &&
          membership.userRole === "contributor" &&
          contributorEmptyState()}
      </div>
    </div>
  );
};

function contributorEmptyState() {
  return (
    <div className="empty-state-text margin-auto">
      {translate("app.institution.curriculum.contactAdmin")}
    </div>
  );
}

function adminEmptyState() {
  const { REDIRECT_URI } = runConfig;
  return (
    <div className="empty-state-text margin-auto">
      <a
        className="empty-state-link"
        href={`${REDIRECT_URI}/admin/configuration_settings/reporting_year`}
      >
        {translate("app.faculty.createReportingYear")}
      </a>
      {translate("app.institution.curriculum.contributor")}
    </div>
  );
}

const NoProgramProposals = () => {
  return (
    <div className="no-plans">
      <div className="no-plans-text">
        {translate("app.institution.curriculum.noProgramProposalsText")}
      </div>
      <div className="no-plans-text-select">
        {translate("app.institution.curriculum.noProposalsSubText")}
      </div>
    </div>
  );
};
const NoCourseProposals = () => {
  return (
    <div className="no-plans">
      <div className="no-plans-text ">
        {translate("app.institution.curriculum.noCourseProposalsText")}
      </div>
      <div className="no-plans-text-select">
        {translate("app.institution.curriculum.noProposalsSubText")}
      </div>
    </div>
  );
};
